import { Mat4 } from "@base/math/Mat4";
import { IVec3, Vec3 } from "@base/math/Vec3";
import { CacheUtil } from "@base/util/Cache";
import { StandardTemplate } from "@biz/definitions/StandardTemplate";
import { ISunSailData } from "@biz/definitions/SunSailDefinitions";
import { SunSailMath } from "@biz/items/sunSail/logic/SunSailMath";
import { PoleHelper } from "./PoleHelper";
import { SunSailLogic } from "./SunSailLogic";
import { TriangleHelper } from "./TriangleHelper";

export class StandardLogic {

  static calculate = CacheUtil.memo(StandardLogic.#calculate, "calculate", { // matrixForLengthPosSelector
    idsFromArgs: (sunSail) => [sunSail.id],
    resultEqualFn: "level3"
  })

  static #calculate(sunSail: ISunSailData): {
    mountPositions: IVec3[];
    polePositions: IVec3[];
    netPositionGroups: IVec3[][];
    groundRotations: number[];
  } {
    if (sunSail.specific?.type !== "standard")
      throw new Error("Standard sail without specific data!");
    const template = StandardTemplate.getTemplate(sunSail.specific.sizeId)!;

    const templatePositions = StandardTemplate.calcStandardTemplatePositions(template);

    const slopes: IVec3[] = [];
    const niveaus: number[] = [];
    const mountHeights: number[] = [];
    const distIns: number[] = [];
    for (let i = 0; i < templatePositions.length; i++) {
      slopes[i] = SunSailLogic.calcMountSlope(sunSail, i, 0);
      niveaus[i] = SunSailLogic.getPoleNiveau(sunSail, i);
      distIns[i] = PoleHelper.getDistInValue(sunSail.poles[i].dist, 0);
      mountHeights[i] = niveaus[i] + slopes[i].y;
    }

    const centerHeight = StandardLogic.calcCenterHeight(templatePositions, mountHeights, distIns);
    const templateHeights: number[] = [];
    for (let i = 0; i < templatePositions.length; i++) {
      templateHeights[i] = StandardLogic.calcTemplateHeight(mountHeights, centerHeight, distIns, templatePositions, i);
    }

    const poleTemplatePositions: IVec3[] = [];
    for (let i = 0; i < templatePositions.length; i++) {
      const tp = templatePositions[i];
      poleTemplatePositions[i] = Vec3.from(tp.x, 0, tp.z)
    }

    const heightCorrected: IVec3[] = [];
    for (let i = 0; i < templatePositions.length; i++) {
      const l = Vec3.len(poleTemplatePositions[i]);
      const x = Math.sqrt(l * l - (templateHeights[i] - centerHeight) ** 2);
      heightCorrected[i] = Vec3.mul(poleTemplatePositions[i], x / l)
    }
    if (heightCorrected.length === 3) {
      const a = Vec3.from(heightCorrected[0].x, templateHeights[0], heightCorrected[0].z)
      const b = Vec3.from(heightCorrected[2].x, templateHeights[2], heightCorrected[2].z)
      const m = Vec3.mid(a, b)
      const dir = Vec3.dir(a, b);
      const l = Vec3.len(poleTemplatePositions[0], poleTemplatePositions[2]);
      const na = Vec3.addScaled(m, dir, -l * 0.5);
      const nb = Vec3.addScaled(m, dir, l * 0.5);
      // const l2 = Vec3.len(na, nb);
      heightCorrected[0] = Vec3.withY(na, 0);
      heightCorrected[2] = Vec3.withY(nb, 0);

      templateHeights[0] = na.y; // Dreieck hack
      templateHeights[2] = nb.y;
    }

    const heightCorrected2: IVec3[] = [];
    heightCorrected2[0] = heightCorrected[0];
    heightCorrected2[2] = heightCorrected[2];
    const b = TriangleHelper.calcPointForDistancesWithY(Vec3.from(heightCorrected2[0].x, templateHeights[0], heightCorrected2[0].z), Vec3.from(heightCorrected2[2].x, templateHeights[2], heightCorrected2[2].z), Vec3.len(templatePositions[0], templatePositions[1]), Vec3.len(templatePositions[1], templatePositions[2]), templateHeights[1], true);
    if (b === undefined)
      throw new Error("Couldn't calculate point B!");
    heightCorrected2[1] = b;
    if (templatePositions.length >= 4) {
      const d = TriangleHelper.calcPointForDistancesWithY(Vec3.from(heightCorrected2[0].x, templateHeights[0], heightCorrected2[0].z), Vec3.from(heightCorrected2[2].x, templateHeights[2], heightCorrected2[2].z), Vec3.len(templatePositions[0], templatePositions[3]), Vec3.len(templatePositions[3], templatePositions[2]), templateHeights[3], false);
      if (d === undefined)
        throw new Error("Couldn't calculate point D!");
      heightCorrected2[3] = d;
    }

    const centerHC2 = Vec3.center(heightCorrected2);
    for (let i = 0; i < heightCorrected2.length; i++)
      heightCorrected2[i] = Vec3.addElements(heightCorrected2[i], -centerHC2.x, 0, -centerHC2.z);

    const netPositions: IVec3[] = [];
    for (let i = 0; i < templatePositions.length; i++) {
      netPositions[i] = Vec3.from(heightCorrected2[i].x, templateHeights[i], heightCorrected2[i].z);
    }

    // transform
    const center = SunSailLogic.calcSunSailLogicalCenter(sunSail);
    const rotationY = sunSail.specific?.rotationY || 0;
    const rotationMatrix = Mat4.createRotationY(rotationY);
    const rotated = netPositions.map((p) => {
      return Vec3.addElements(Vec3.applyMatrix4(p, rotationMatrix), center.x, 0, center.z)
    })

    const polePositions: IVec3[] = [];
    const mountPositions: IVec3[] = [];
    const groundRotations: number[] = [];
    for (let i = 0; i < templatePositions.length; i++) {
      const half = TriangleHelper.calcHalfVector(rotated[i], rotated[(i + rotated.length - 1) % rotated.length], rotated[(i + 1) % rotated.length])
      const mp = Vec3.addScaled(rotated[i], half, -distIns[i]);
      const mpGround = Vec3.from(mp.x, 0/* mountHeights[i]*/, mp.z);
      const slopeVec = Vec3.normalized(Vec3.withY(half, 0));
      groundRotations.push(SunSailMath.calcGroundRotationFromVec(slopeVec));
      polePositions[i] = Vec3.addElements(Vec3.addScaled(mpGround, slopeVec, slopes[i].z), 0, niveaus[i], 0); // slope.x?
      mountPositions[i] = Vec3.from(mpGround.x, mountHeights[i], mpGround.z)
    }

    return {
      mountPositions,
      polePositions,
      netPositionGroups: [rotated],
      groundRotations
    }
  }

  static calcCenterHeight(templatePositions: IVec3[], heights: number[], distIns: number[]): number {
    let distSum = 0;
    let distsFactor = 1;
    const dists: number[] = [];
    for (let i = 0; i < templatePositions.length; i++) {
      dists[i] = distIns[i] + Vec3.len(templatePositions[i]);
      distsFactor = distsFactor * dists[i];
    }
    let totalFactor = 0;
    for (let i = 0; i < dists.length; i++) {
      const f = distsFactor / dists[i];
      distSum += f * heights[i];
      totalFactor += f;
    }
    const m = distSum /= totalFactor;
    return m;
  }

  static calcTemplateHeight(mountHeights: number[], centerHeight: number, distIns: number[], templatePositions: IVec3[], index: number): number {
    return mountHeights[index] - (mountHeights[index] - centerHeight) * distIns[index] / (distIns[index] + Vec3.len(templatePositions[index]));
  }
}